<script>
  import { fly } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import RightPane from '../Panes/RightPane.svelte';
  import { getEgresoDets } from '../../models/egresoDetModel';
  import { getEgresoEscoltas, getEgresoCorredores, getEgresoPickers } from '../../models/egresoCabModel';
  import { authStore } from '../../lib/authStore';
  
  const dispatch = createEventDispatcher();
  
  export let title;
  export let status;
  export let items;

  let selectedItem = null;
  let draggedItem = null;
  let isDragOver = false;
  let isAdmin = false;

  // Check admin role from localStorage
  function checkAdminRole() {
    const roleData = localStorage.getItem("pb_user_role");
    if (roleData) {
      try {
        const role = JSON.parse(roleData);
        isAdmin = role.descrip === 'Administrador';
      } catch (error) {
        isAdmin = false;
      }
    } else {
      isAdmin = false;
    }
  }

  // Check role when component mounts and periodically
  onMount(() => {
    checkAdminRole();
    // Check again after a short delay in case role was still loading
    setTimeout(checkAdminRole, 1000);
  });

  async function handleClick(item) {
    // Si `item` es un objeto (no un array), obtener directamente los detalles de egresoDet
    const egresoDets = await getEgresoDets(item.id, 1, 100);  // Ajusta la paginación de egresoDet si es necesario
    const egresoEscolta = await getEgresoEscoltas(item.id, 1, 100);  // Ajusta la paginación de egresoEscolta si es necesario
    const egresoCorredor = await getEgresoCorredores(item.id, 1, 100);
    const egresoPicker = await getEgresoPickers(item.id, 1, 100);
    // Actualizar selectedItem con el egresoCab y sus detalles
    selectedItem = {
      ...item,  // Mantener los datos del egresoCab
      egresoDet: egresoDets.items,  // Agregar los detalles de egresoDet
      escoltas: egresoEscolta.items,  // Agregar los detalles de egresoEscolta
      corredores: egresoCorredor.items, // Agregar los detalles de egresoCorredor
      pickers: egresoPicker.items, // Agregar los detalles de egresoPicker
    };

    console.log("selectedItem",selectedItem)
  }

  function closePane() {
    selectedItem = null;
  }

  function formatDate(dateStr) {
    // Create date object from the input string (UTC from PocketBase)
    const date = new Date(dateStr);
    if (isNaN(date)) return '';
    
    // Convert from UTC to Guatemala timezone (UTC-6)
    const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
    
    // Format date components
    const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
    const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
    const year = guatemalaDate.getUTCFullYear();
    
    // Get hours in 12-hour format
    let hours = guatemalaDate.getUTCHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    const minutes = String(guatemalaDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(guatemalaDate.getUTCSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year}, ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`;
  }

  // Simple function to truncate text
  function truncateText(text, maxLength = 50) {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  function getEscoltaNames(item) {
    if (Array.isArray(item.escolta_names) && item.escolta_names.length > 0) {
      return item.escolta_names;
    }

    if (item.escolta_name && item.escolta_name !== 'Sin escolta') {
      return item.escolta_name.split(',').map((name) => name.trim()).filter(Boolean);
    }

    return [];
  }

  const statusColors = {
    NOT_READY: 'bg-red-200 border-red-300 text-red-700',
    PACKING: 'bg-yellow-500 border-yellow-600 text-yellow-900',
    PARTIALLY_READY: 'bg-orange-200 border-orange-300 text-orange-700',
    READY: 'bg-teal-200 border-teal-300 text-teal-700',
    ON_ROUTE: 'bg-lightBlue-200 border-lightBlue-300 text-lightBlue-700',
    DELIVERED: 'bg-emerald-200 border-emerald-300 text-emerald-700',
    RETURNED: 'bg-red-200 border-red-300 text-red-700',
    DEFAULT: 'bg-gray-100 border-gray-300 text-gray-700',
  };

  function getStatusColor() {
    return statusColors[status] || statusColors.DEFAULT;
  }

  const noteThemes = {
    NOT_READY: { bg: '#ffe8a3', bgDark: '#ffd966', ink: '#5c4a00' },
    PACKING: { bg: '#fff59d', bgDark: '#ffe566', ink: '#5c5200' },
    PARTIALLY_READY: { bg: '#ffd4a8', bgDark: '#ffb366', ink: '#6b3a00' },
    READY: { bg: '#b8f2e6', bgDark: '#7ee8d5', ink: '#0f5132' },
    ON_ROUTE: { bg: '#b8dcff', bgDark: '#7ec0ff', ink: '#0c3d6b' },
    DELIVERED: { bg: '#c8f7c5', bgDark: '#8ee889', ink: '#1b5e20' },
    CANCELLED: { bg: '#eceff1', bgDark: '#cfd8dc', ink: '#455a64' },
    RETURNED: { bg: '#ffcdd2', bgDark: '#ef9a9a', ink: '#7f0000' },
    DEFAULT: { bg: '#fff9c4', bgDark: '#fff176', ink: '#4a4a4a' },
  };

  function getNoteTheme() {
    return noteThemes[status] || noteThemes.DEFAULT;
  }

  function getNoteTilt(id = '') {
    let hash = 0;
    for (let i = 0; i < id.length; i += 1) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    return (hash % 7) - 3;
  }

  function getNoteStyle(id) {
    const theme = getNoteTheme();
    return `--note-bg: ${theme.bg}; --note-bg-dark: ${theme.bgDark}; --note-ink: ${theme.ink}; --note-tilt: ${getNoteTilt(id)}deg;`;
  }

  let draggingId = null;

  function handleLocationUpdate(event) {
      console.log("Event payload in CardStatus:", event.detail);
      const { id, geo_target_ref, geo_delivery_ref } = event.detail;

      // Update the local state
      items = items.map(item => {
          if (item.id === id) {
            return {
                ...item,
                ...(geo_target_ref && { geo_target_ref }),
                ...(geo_delivery_ref && { geo_delivery_ref })
            };
          }
          return item;
      });

      // Optionally, dispatch the event to the parent component
      dispatch('updateLocation', event.detail);
  }
  

  function handleEgresoEscoltaUpdate(event) {
    console.log("Event payload in CardStatus (Escoltas Update):", event.detail);
    const { egresoId, escoltas } = event.detail;

    // Ensure the correct keys are updated in the items array
    items = items.map(item => {
      if (item.id === egresoId) {
        return {
          ...item,
          escoltas: escoltas // Update escoltas for the matching item
        };
      }
      return item;
    });

    console.log("Updated items in CardStatus (Escoltas):", items);

    // Dispatch the escoltas update to the parent component or store
    dispatch('updateEgresoEscolta', {
      egresoId: egresoId,
      escoltas: escoltas
    });
  }

  function handleDragStart(event, item) {
    // Only allow drag if user is admin
    if (!isAdmin) {
      event.preventDefault();
      return;
    }
    
    draggedItem = item;
    draggingId = item.id;
    event.dataTransfer.setData('text/plain', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnd(event) {
    draggingId = null;
    draggedItem = null;
  }

  function handleDragOver(event) {
    // Only allow drop if user is admin
    if (!isAdmin) {
      event.preventDefault();
      return;
    }
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    isDragOver = true;
  }

  function handleDragLeave(event) {
    // Only set isDragOver to false if we're leaving the drop zone entirely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      isDragOver = false;
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    isDragOver = false;
    
    // Only allow drop if user is admin
    if (!isAdmin) {
      return;
    }
    
    try {
      const droppedItem = JSON.parse(event.dataTransfer.getData('text/plain'));
      
      // Check if the item is being dropped in a different status column
      if (droppedItem.sta !== status) {
        // Update the item's status
        const updatedItem = { ...droppedItem, sta: status };
        
        // Dispatch the status change event to parent component
        dispatch('statusChange', {
          itemId: droppedItem.id,
          oldStatus: droppedItem.sta,
          newStatus: status,
          item: updatedItem
        });
        
        // Remove the item from the current column if it was dropped here
        if (droppedItem.sta !== status) {
          items = items.filter(item => item.id !== droppedItem.id);
        }
      }
    } catch (error) {
      console.error('Error processing dropped item:', error);
    }
  }
</script>

<style>
  .notes-board {
    background-color: rgba(255, 255, 255, 0.28);
    background-image: radial-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px);
    background-size: 10px 10px;
  }

  .sticky-note {
    position: relative;
    border: none;
    border-radius: 2px 2px 14px 2px;
    padding: 0.9rem 0.95rem 1.05rem;
    margin-bottom: 1.35rem;
    background: linear-gradient(
      145deg,
      var(--note-bg) 0%,
      var(--note-bg-dark) 100%
    );
    color: var(--note-ink);
    box-shadow:
      1px 2px 2px rgba(0, 0, 0, 0.12),
      3px 5px 10px rgba(0, 0, 0, 0.14),
      0 12px 18px rgba(0, 0, 0, 0.06);
    transform: rotate(var(--note-tilt, 0deg));
    transition: transform 0.22s ease, box-shadow 0.22s ease, z-index 0s;
    list-style: none;
  }

  .sticky-note::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 18px 18px 0;
    border-color: transparent rgba(0, 0, 0, 0.12) transparent transparent;
  }

  .sticky-note::after {
    content: '';
    position: absolute;
    top: -7px;
    left: 50%;
    width: 12px;
    height: 12px;
    margin-left: -6px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #ff6b6b, #c62828);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
    opacity: 0.85;
  }

  .sticky-note:hover {
    transform: rotate(0deg) translateY(-5px) scale(1.015);
    box-shadow:
      2px 4px 4px rgba(0, 0, 0, 0.14),
      6px 10px 18px rgba(0, 0, 0, 0.16),
      0 16px 28px rgba(0, 0, 0, 0.08);
    z-index: 5;
  }

  .sticky-note.is-dragging {
    opacity: 0.72;
    transform: rotate(2deg) scale(0.97);
    z-index: 20;
    box-shadow:
      4px 8px 12px rgba(0, 0, 0, 0.2),
      0 18px 28px rgba(0, 0, 0, 0.12);
  }

  .note-badge {
    background: rgba(255, 255, 255, 0.72);
    color: var(--note-ink);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .note-label {
    color: rgba(0, 0, 0, 0.55);
  }

  .note-value {
    color: var(--note-ink);
  }

  .note-date {
    color: rgba(0, 0, 0, 0.5);
    font-style: italic;
  }

  .note-escolta {
    background: rgba(255, 255, 255, 0.55);
    color: var(--note-ink);
    border: 1px solid rgba(0, 0, 0, 0.08);
  }

  .drag-over {
    background-color: rgba(59, 130, 246, 0.1);
    border-color: rgb(59, 130, 246);
  }
</style>

  <div
    class={`relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded border ${getStatusColor()} ${isDragOver ? 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50' : ''}`}
    style="height: 480px;"
    on:dragover={handleDragOver}
    on:dragleave={handleDragLeave}
    on:drop={handleDrop}
  >
  <div class={`rounded-t mb-0 px-4 py-3 border-0 ${getStatusColor()}`}>
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <h3 class="font-semibold text-base">{title} ({items.length})</h3>
      </div>
    </div>
  </div>

  <div class="notes-board block w-full overflow-y-auto p-4 pt-5" style="height: calc(100% - 116px);">
    {#if items.length > 0}
      <ul class="list-none m-0 p-0">
        {#each items as item (item.id)}
          <li
            transition:fly={{ y: 20, duration: 600 }}
            class="sticky-note {isAdmin ? 'cursor-grab' : 'cursor-pointer'} {draggingId === item.id ? 'is-dragging' : ''}"
            style={getNoteStyle(item.id)}
            draggable={isAdmin}
            on:click={() => handleClick(item)}
            on:dragstart={(e) => handleDragStart(e, item)}
            on:dragend={handleDragEnd}
          >
            <div class="flex justify-between items-start mb-2 pt-1">
              <div class="overflow-x-auto flex-1 mr-2">
                <span class="font-bold text-sm leading-tight whitespace-nowrap" title={item.nombre}>{item.nombre}</span>
              </div>
              <span class="note-badge text-xs px-2 py-1 rounded flex-shrink-0">{item.numero}</span>
            </div>

            <div class="note-date text-xs mb-3">{formatDate(item.created)}</div>

            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Método:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.delivery_method || 'Sin asignar'}</span>
                </div>
              </div>
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Picker:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.picker_name || 'Sin picker'}</span>
                </div>
              </div>
              <div class="flex justify-between">
                <span class="note-label text-xs font-medium flex-shrink-0">Delivery:</span>
                <div class="overflow-x-auto flex-1 ml-2 text-right">
                  <span class="note-value text-xs font-semibold whitespace-nowrap">{item.delivery_name || 'Sin asignar'}</span>
                </div>
              </div>
              {#if getEscoltaNames(item).length > 0}
                <div class="flex justify-between">
                  <span class="note-label text-xs font-medium flex-shrink-0">Escoltas:</span>
                  <div class="overflow-x-auto flex-1 ml-2">
                    <div class="flex flex-wrap gap-1 justify-end">
                      {#each getEscoltaNames(item) as escolta}
                        <span class="note-escolta text-xs px-2 py-1 rounded">{escolta}</span>
                      {/each}
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    {:else}
        <div class="flex items-center justify-center h-32 text-blueGray-400 text-sm border-2 border-dashed border-blueGray-300 rounded-lg">
          <p>{isAdmin ? `Drop items here to change status to: ${status}` : 'Only administrators can drag and drop orders'}</p>
        </div>
      {/if}
  </div>

  <div class={`rounded-b px-4 py-3 border-t mt-auto ${getStatusColor()}`}>
    <div class="text-blueGray-500 text-sm">
      Total: {items.length} items
    </div>
  </div>
</div>

<RightPane 
  {selectedItem}
  {closePane}
  {status}
  on:updateLocation={handleLocationUpdate}
  on:updateEgresoEscolta={handleEgresoEscoltaUpdate}
/>